import { useEffect, useState, useMemo } from "react";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarDays, Clock, DollarSign, CheckCircle2, PhoneCall, Plus, FileText, Wrench, X } from "lucide-react";

const CATEGORIES = [
  "Hardware Troubleshooting",
  "Software & OS Fixes",
  "Virus & Malware Removal",
  "Network & Wi-Fi Optimization",
  "Security Health Check",
  "Custom PC & Peripheral Setup",
  "Cloud Backup Setup & Secure",
  "Speed Up & Optimize",
];
const CATEGORY_DESCRIPTIONS = {
  "Cloud Backup Setup & Secure": "We audit your existing cloud accounts (Google Drive, iCloud, OneDrive) and set up automatic backup for your photos, documents, and contacts — so your data is always protected without needing new storage. Includes a walkthrough so you know it's working.",
  "Speed Up & Optimize": "Is your device running slow? Our technician will diagnose and fix performance issues — clearing junk files, optimizing startup programs, updating drivers, and fine-tuning your system so it runs like new. Works for laptops, PCs, and mobile devices.",
};
const CATEGORY_PRICE_LABELS = {
  "Cloud Backup Setup & Secure": "₹799",
  "Speed Up & Optimize": "Starting from ₹599, quoted on assessment",
};
const DEFAULT_PRICE_LABEL = "₹599";
const TIME_SLOTS = [
  { value: "09:00", label: "9:00 AM – 11:00 AM" },
  { value: "11:00", label: "11:00 AM – 1:00 PM" },
  { value: "13:00", label: "1:00 PM – 3:00 PM" },
  { value: "15:00", label: "3:00 PM – 5:00 PM" },
  { value: "17:00", label: "5:00 PM – 7:00 PM" },
];

const statusStyle = {
  payment_pending: "bg-amber-100 text-amber-800 border-amber-300",
  scheduled: "bg-blue-100 text-blue-700 border-blue-300",
  in_progress: "bg-blue-600 text-slate-900 border-blue-700",
  completed: "bg-slate-800 text-slate-900 border-slate-900",
  escalated: "bg-red-100 text-red-700 border-red-300",
  cancelled: "bg-slate-50 text-slate-500 border-slate-300",
};
const statusLabel = { payment_pending: "Payment pending", scheduled: "Scheduled", in_progress: "In progress", completed: "Completed", escalated: "Escalated", cancelled: "Cancelled" };

function StatCard({ icon: Icon, label, value, tint, testid }) {
  return (
    <div data-testid={testid} className={`${tint} border border-slate-200 rounded-2xl p-5 soft-card`}>
      <Icon className="w-6 h-6 text-slate-900" strokeWidth={2.2} />
      <div className="font-display text-3xl font-extrabold mt-3 text-slate-900">{value}</div>
      <div className="text-sm font-semibold text-slate-700 mt-1">{label}</div>
    </div>
  );
}

export default function UserDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [openBook, setOpenBook] = useState(false);
  useEffect(() => {
    const tawk = window.Tawk_API;
    if (openBook) { tawk?.hideWidget?.(); } else { tawk?.showWidget?.(); }
  }, [openBook]);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try {
      const [s, ss] = await Promise.all([api.get("/sessions/my/stats"), api.get("/sessions/my")]);
      setStats(s.data);
      setSessions(ss.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    try {
      await api.post(`/sessions/${id}/cancel`);
      toast.success("Session cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Hi {user?.name?.split(" ")[0]} 👋</h1>
            <p className="text-slate-500 mt-1">Book a fix or check on your case logs.</p>
          </div>
          <Dialog open={openBook} onOpenChange={setOpenBook}>
            <DialogTrigger asChild>
              <Button data-testid="schedule-session-btn" className="bg-brand hover:bg-brand-dark border border-slate-200 shadow-btn font-bold h-12 px-5">
                <Plus className="w-4 h-4 mr-1" /> Schedule new session
              </Button>
            </DialogTrigger>
            <BookSessionDialog onClose={() => { setOpenBook(false); load(); }} />
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          <StatCard testid="stat-total-sessions" icon={CalendarDays} tint="bg-blue-100" label="Total sessions" value={stats?.total_sessions ?? 0} />
          <StatCard testid="stat-completed" icon={CheckCircle2} tint="bg-blue-100" label="Completed" value={stats?.completed ?? 0} />
          <StatCard testid="stat-scheduled" icon={Clock} tint="bg-blue-100" label="Upcoming" value={stats?.scheduled ?? 0} />
        <StatCard testid="stat-total-paid" icon={DollarSign} tint="bg-slate-200" label="Total paid" value={`₹${(stats?.total_paid ?? 0).toFixed(0)}`} />
        </div>

        {/* Sessions list */}
        <div className="soft-card p-6">
          <Tabs defaultValue="all">
            <TabsList data-testid="sessions-tabs" className="bg-slate-50 border border-slate-200 p-1 rounded-xl w-full max-w-full overflow-x-auto justify-start">
              <TabsTrigger value="all" data-testid="tab-all" className="shrink-0">All</TabsTrigger>
              <TabsTrigger value="scheduled" data-testid="tab-scheduled" className="shrink-0">Scheduled</TabsTrigger>
              <TabsTrigger value="in_progress" data-testid="tab-in_progress" className="shrink-0">In progress</TabsTrigger>
              <TabsTrigger value="completed" data-testid="tab-completed" className="shrink-0">Completed</TabsTrigger>
              <TabsTrigger value="cancelled" data-testid="tab-cancelled" className="shrink-0">Cancelled</TabsTrigger>
            </TabsList>

            {["all", "scheduled", "in_progress", "completed", "cancelled"].map((tab) => {
              const list = tab === "all" ? sessions : sessions.filter((s) => s.status === tab);
              return (
                <TabsContent key={tab} value={tab} className="mt-6">
                  {list.length === 0 ? (
                    <div className="text-center py-14 text-slate-500">
                      <Wrench className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      No sessions here yet. Book your first fix!
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {list.map((s) => (
                        <div key={s.id} data-testid={`session-row-${s.id}`} className="border border-slate-200 hover:border-blue-300 hover:bg-slate-50 rounded-xl p-4 flex flex-wrap items-center justify-between gap-4 transition-colors">
                          <div className="flex-1 min-w-[220px]">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="font-display font-bold text-slate-900">{s.category}</div>
                              <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${statusStyle[s.status]}`}>{statusLabel[s.status]}</span>
                              {s.payment_status === "paid" && (
                                <span data-testid={`pay-badge-${s.id}`} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border bg-emerald-100 text-emerald-800 border-emerald-300">
                                  ₹{(s.advance_amount || 0).toFixed(0)} paid
                                </span>
                              )}
                              {s.payment_status === "pending" && (
                                <span data-testid={`pay-badge-${s.id}`} className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border bg-amber-100 text-amber-800 border-amber-300">
                                  Payment pending
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                              <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" /> {new Date(s.scheduled_at).toLocaleString()}</span>
                              <span className="flex items-center gap-1"><PhoneCall className="w-4 h-4" /> {s.phone}</span>
                              {s.technician && <span className="flex items-center gap-1 font-medium text-slate-700">👤 {s.technician}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" data-testid={`view-log-btn-${s.id}`} onClick={() => setDetail(s)} className="border border-slate-200 font-semibold">
                              <FileText className="w-4 h-4 mr-1" /> View case log
                            </Button>
                            {s.status === "scheduled" && (
                              <Button variant="outline" size="sm" data-testid={`cancel-btn-${s.id}`} onClick={() => cancel(s.id)} className="border border-slate-200 text-slate-900 hover:bg-slate-50 font-semibold">
                                <X className="w-4 h-4 mr-1" /> Cancel
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </div>

      {/* Case log drawer */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent data-testid="case-log-modal" className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle className="font-display text-2xl">Case log</SheetTitle>
          </SheetHeader>
          {detail && (
            <div className="mt-4 space-y-4">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${statusStyle[detail.status]}`}>{statusLabel[detail.status]}</span>
                <span className="text-sm text-slate-500">{new Date(detail.scheduled_at).toLocaleString()}</span>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Category</div>
                <div className="font-semibold text-slate-900 mt-1">{detail.category}</div>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Issue described</div>
                <div className="mt-1 bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">{detail.issue_description}</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Technician</div>
                  <div className="font-semibold mt-1">{detail.technician || "— unassigned"}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Duration</div>
                  <div className="font-semibold mt-1">{detail.duration_minutes || 0} min</div>
                </div>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Resolution notes</div>
                <div className="mt-1 bg-blue-100 border border-blue-200 rounded-lg p-3 text-sm whitespace-pre-wrap">{detail.resolution_notes || "Pending — will be added after the call."}</div>
              </div>
              <div className="border-t pt-4 flex items-center justify-between">
                <div className="text-sm text-slate-500">Session fee</div>
                <div className="font-display font-bold text-xl">
                  {detail.price == null ? "Starting from ₹599, quoted on assessment" : `₹${detail.price.toFixed(0)}`}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function BookSessionDialog({ onClose }) {
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState(TIME_SLOTS[0].value);
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [phone, setPhone] = useState("+91 ");
  const [desc, setDesc] = useState("");
  const [loading, setLoading] = useState(false);

  const activeSlot = TIME_SLOTS.find((s) => s.value === time) || TIME_SLOTS[0];

  const scheduled = useMemo(() => {
    if (!date) return null;
    const [h, m] = time.split(":");
    const d = new Date(date);
    d.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
    return d;
  }, [date, time]);

  const submit = async () => {
    if (!scheduled) { toast.error("Pick a date"); return; }
    if (phone.replace(/\D/g, "").length < 10) { toast.error("Add a phone number"); return; }
    if (!desc.trim()) { toast.error("Describe the issue briefly"); return; }
    const priceLabel = CATEGORY_PRICE_LABELS[category] || DEFAULT_PRICE_LABEL;
    const isTbd = /quoted on assessment/i.test(priceLabel);
    setLoading(true);
    try {
      const payload = {
        scheduled_at: scheduled.toISOString(),
        phone: phone.trim(),
        category,
        issue_description: desc.trim(),
      };
      // TBD-priced services skip the Razorpay flow — admin quotes later.
      if (isTbd) {
        await api.post("/sessions", payload);
        toast.success("Session booked. Our team will quote the fix after a quick assessment.");
        onClose();
        return;
      }
      const { data } = await api.post("/payments/create-order", payload);
      if (!window.Razorpay) {
        toast.error("Payment library didn't load. Please refresh and try again.");
        return;
      }
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Yonsii Geek Support",
        description: `${category} — 50% advance (₹${data.advance_amount} of ₹${data.total_price.toFixed(0)})`,
        order_id: data.order_id,
        prefill: data.prefill || {},
        theme: { color: "#0A1628" },
        handler: async (res) => {
          try {
            await api.post("/payments/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success(`Paid ₹${data.advance_amount} — your session is confirmed.`);
            onClose();
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Payment captured but we couldn't confirm it. Admin has been notified.");
          }
        },
        modal: {
          ondismiss: () => {
            toast.message("Payment cancelled. Your slot is held for 10 mins — you can resume anytime.");
          },
        },
      });
      rzp.on("payment.failed", (res) => {
        toast.error(res?.error?.description || "Payment failed. Please try again.");
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto overscroll-contain max-sm:inset-0 max-sm:left-0 max-sm:top-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:max-w-full max-sm:rounded-none max-sm:p-4 max-sm:pb-24">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">Book a tech session</DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label className="font-semibold">Pick a date</Label>
          <div className="mt-2 border border-slate-200 rounded-xl p-2 block w-full overflow-x-auto">
            <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} data-testid="session-date-picker" />
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="font-semibold">Time slot</Label>
            <div data-testid="session-time-select" role="radiogroup" className="mt-1 grid grid-cols-2 gap-2">
              {TIME_SLOTS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  role="radio"
                  aria-checked={time === t.value}
                  data-testid={`time-${t.value}`}
                  onClick={() => setTime(t.value)}
                  className={`h-11 px-2 rounded-lg border text-xs font-semibold touch-manipulation transition-colors ${time === t.value ? "bg-[#0A1628] text-white border-[#0A1628]" : "bg-white text-slate-800 border-slate-200 hover:border-slate-400"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-1">Selected: <span data-testid="selected-slot-label" className="font-semibold text-slate-900">{activeSlot.label}</span></p>
          </div>
          <div>
            <Label className="font-semibold">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="session-category-select" className="mt-1 border border-slate-200 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            {CATEGORY_DESCRIPTIONS[category] && (
              <p data-testid="category-description" className="text-xs text-slate-500 mt-2 leading-relaxed bg-slate-50 border border-slate-200 rounded-lg p-3">
                {CATEGORY_DESCRIPTIONS[category]}
              </p>
            )}
          </div>
          <div>
            <Label className="font-semibold">Phone (we'll call you)</Label>
            <Input data-testid="session-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98765 43210" className="mt-1 border border-slate-200 h-11" />
          </div>
          <div>
            <Label className="font-semibold">Describe the issue</Label>
            <Textarea data-testid="session-desc-input" value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} className="mt-1 border border-slate-200" />
          </div>
          <div className="bg-blue-100 border border-slate-200 rounded-xl p-3 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold">Session fee</div>
              <div className="text-[11px] text-slate-600 mt-0.5">
                {/quoted on assessment/i.test(CATEGORY_PRICE_LABELS[category] || DEFAULT_PRICE_LABEL)
                  ? "Pay after our technician quotes the fix"
                  : "Pay 50% now, balance after the fix"}
              </div>
            </div>
            <div data-testid="session-fee-value" className="font-display font-extrabold text-lg">
              {CATEGORY_PRICE_LABELS[category] || DEFAULT_PRICE_LABEL}
            </div>
          </div>
        </div>
      </div>
      <DialogFooter className="sticky bottom-0 bg-white pt-3 pb-2 -mx-4 px-4 border-t border-slate-200 z-10">
        <Button onClick={submit} disabled={loading} data-testid="session-submit-btn" className="bg-brand hover:bg-brand-dark border border-slate-200 shadow-btn font-bold h-11 px-6">
          {loading
            ? "Opening payment…"
            : /quoted on assessment/i.test(CATEGORY_PRICE_LABELS[category] || DEFAULT_PRICE_LABEL)
              ? "Confirm booking"
              : "Pay 50% & confirm"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
